import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Sparkles, Users, GraduationCap, Music2, Crown, Gift } from "lucide-react";
import { Link } from "react-router-dom";

export function SubscribePage() {
  const { user, subscription, refresh, setUser } = useAuth();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [tier, setTier] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    apiClient.get("/subscription/pricing").then((r) => setTier(r.data)).catch(() => {});
  }, []);

  const start = async (opts = {}) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const body = { origin_url: origin, plan: opts.premium ? "premium" : "base" };
      let r;
      try {
        // New recurring-subscription endpoint (preferred — requires real Stripe key).
        r = await apiClient.post("/subscription/checkout", body);
      } catch (err) {
        const code = err?.response?.status;
        const detailCode = err?.response?.data?.detail?.code;
        // Already have an active sub → bounce to the billing portal instead.
        if (code === 409 && detailCode === "subscription_exists") {
          try {
            const p = await apiClient.post("/subscription/billing-portal", {});
            if (p.data?.url) { window.location.href = p.data.url; return; }
          } catch { /* fall through */ }
          toast.info(t("You already have an active subscription."));
          setBusy(false);
          return;
        }
        // Stripe not configured (mock key in preview) → fall back to legacy one-time checkout.
        if (code === 503) {
          r = await apiClient.post("/payments/checkout", {
            origin_url: origin, premium: !!opts.premium,
          });
        } else {
          throw err;
        }
      }
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || t("Unable to start checkout"));
      setBusy(false);
    }
  };

  const openBillingPortal = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/subscription/billing-portal", { return_url: window.location.href });
      if (r.data?.url) { window.location.href = r.data.url; return; }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Couldn't open billing portal"));
    }
    setBusy(false);
  };

  const cancelSubscription = async () => {
    if (!window.confirm(t("Cancel your subscription at the end of this billing period? You'll keep access until then."))) return;
    try {
      await apiClient.post("/subscription/cancel", {});
      await refresh();
      toast.success(t("Subscription will cancel at period end. You can reactivate anytime."));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Cancel failed")); }
  };

  const reactivateSubscription = async () => {
    try {
      await apiClient.post("/subscription/reactivate");
      await refresh();
      toast.success(t("Subscription reactivated."));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Reactivate failed")); }
  };

  const upgradeToTeacher = async () => {
    if (!window.confirm(t("Switch to a teacher account? Your 30-day trial will restart so you can onboard students risk-free."))) return;
    setUpgrading(true);
    try {
      const r = await apiClient.post("/auth/upgrade-to-teacher");
      if (r.data.token) localStorage.setItem("ptp_token", r.data.token);
      await refresh();
      toast.success(t("You're now a teacher! Fresh 30-day trial started. Add your first student!"));
      nav("/teacher");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Upgrade failed"));
    } finally { setUpgrading(false); }
  };

  const isSoloStudent = user?.role === "student" && user?.solo_learner;
  const defaultBase = isSoloStudent ? 2.99 : 9.99;
  const price = tier?.price_usd ?? defaultBase;
  const isPremium = !!tier?.premium;

  const subscribePremium = () => start({ premium: true });
  const subscribeBase    = () => start({ premium: false });

  return (
    <div className="max-w-3xl mx-auto" data-testid="subscribe-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-3"
             style={{ background: "#fff4cc", color: "#7a5a00" }}>
          <Sparkles className="w-3 h-3" /> {t("Keep your studio running")}
        </div>
        <h1 className="font-display text-5xl font-bold">{t("Subscribe to Octave")}</h1>
        <p className="mt-3" style={{ color: "var(--app-text-muted)" }}>
          {subscription?.trial ? `${t("You have")} ${subscription.trial_days_left} ${t("free trial days left.")}` : subscription?.active ? t("Your subscription is active.") : t("Your trial has ended — subscribe to continue.")}
        </p>
      </div>

      {/* Active-subscription management panel — only when we have a real Stripe Subscription
          (current key uses one-time payments so this is hidden until we upgrade to recurring). */}
      {user?.stripe_subscription_id && user?.stripe_status && (
        <div
          className="mb-6 rounded-3xl p-6 border-2"
          data-testid="active-subscription-panel"
          style={{
            borderColor: user.stripe_status === "active" || user.stripe_status === "trialing"
              ? "var(--app-primary)"
              : "#e63946",
            background: user.stripe_status === "active" || user.stripe_status === "trialing"
              ? "rgba(0,119,182,0.06)"
              : "rgba(230,57,70,0.06)",
          }}
        >
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center rounded-xl w-12 h-12 shrink-0"
                  style={{ background: "var(--app-primary)", color: "#fff" }}>
              <CheckCircle2 className="w-6 h-6" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold">
                {user.premium ? t("Studio Pro") : t("Octave Base")}
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full align-middle uppercase font-bold"
                      data-testid="active-subscription-status"
                      style={{ background: "var(--app-primary)", color: "#fff" }}>
                  {user.stripe_status}
                </span>
                {user.cancel_at_period_end && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full align-middle uppercase font-bold"
                        style={{ background: "#e63946", color: "#fff" }}>
                    {t("cancelling")}
                  </span>
                )}
              </div>
              <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {user.cancel_at_period_end
                  ? `${t("Access continues until")} ${subscription?.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : t("end of period")}. ${t("Reactivate anytime.")}`
                  : subscription?.expires_at
                    ? `${t("Renews on")} ${new Date(subscription.expires_at).toLocaleDateString()}.`
                    : t("Active.")}
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button onClick={openBillingPortal} disabled={busy} className="btn-pill btn-outline h-10" data-testid="open-billing-portal">
                  {t("Manage billing")}
                </Button>
                {user.cancel_at_period_end ? (
                  <Button onClick={reactivateSubscription} className="btn-pill btn-primary h-10" data-testid="reactivate-subscription">
                    {t("Reactivate")}
                  </Button>
                ) : (
                  <Button onClick={cancelSubscription} className="btn-pill btn-outline h-10" data-testid="cancel-subscription">
                    {t("Cancel at period end")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-3xl p-8 border-2" style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}>
        <div className="flex items-baseline gap-2 font-display">
          <div className="text-6xl font-bold" data-testid="price-display">${price.toFixed(2)}</div>
          <div className="text-lg" style={{ color: "var(--app-text-muted)" }}>{t("/ month")}</div>
          {isPremium && (
            <span className="ml-3 px-2 py-1 rounded-full text-xs font-bold align-middle"
                  style={{ background: "var(--app-accent)", color: "#0a0a0a" }} data-testid="premium-badge">{t("STUDIO PRO")}</span>
          )}
        </div>

        {tier && (
          <div className="mt-4 rounded-2xl p-4" style={{ background: "var(--app-surface-2)" }} data-testid="tier-breakdown">
            <div className="text-sm space-y-1" style={{ color: "var(--app-text-muted)" }}>
              <div>• {t("Base plan:")} <b style={{ color: "var(--app-text)" }}>${tier.base_price_usd.toFixed(2)}</b>/mo</div>
              <div>• <b style={{ color: "var(--app-text)" }}>{t("Unlimited instruments")}</b>
                   {!isSoloStudent && <> {t("· up to")} {isPremium ? <b style={{ color: "var(--app-text)" }}>{t("unlimited")}</b> : <b style={{ color: "var(--app-text)" }}>{tier.student_limit}</b>} {t("students")}</>}
              </div>
              {isPremium && (
                <div>• <b style={{ color: "var(--app-text)" }}>+${tier.premium_addon_usd.toFixed(2)}</b>/mo {t("for Studio Pro perks (unlimited students, custom instruments, themes, background)")}</div>
              )}
            </div>
          </div>
        )}

        <ul className="mt-6 space-y-3">
          {[
            t("Smart calendar (month & day view) with auto-populated lessons"),
            t("Practice streaks, punch cards & rewards"),
            t("Songs + Scales tracking per instrument"),
            t("Recitals, invoicing, resource hub, metrics"),
            t("Fun student dashboard + parent access"),
          ].map((f) => (
            <li key={f} className="flex gap-2 items-start"><CheckCircle2 className="w-5 h-5 mt-0.5" style={{ color: "var(--app-primary)" }} /> {f}</li>
          ))}
        </ul>

        <Button onClick={subscribeBase} disabled={busy} className="mt-8 w-full h-14 btn-pill btn-primary justify-center text-lg" data-testid="start-checkout">
          {busy ? t("Redirecting...") : `${t("Subscribe for")} $${(tier?.base_price_usd ?? defaultBase).toFixed(2)}${t("/ month")}`}
        </Button>
        <p className="text-xs mt-3 text-center" style={{ color: "var(--app-text-muted)" }}>
          {t("Auto-renews monthly. Cancel anytime — you'll keep access until period end.")}
        </p>
      </div>

      {tier && (
        <div className="mt-6 rounded-3xl p-6 border-2" data-testid="premium-box"
             style={{
               borderColor: isPremium ? "var(--app-accent)" : "var(--app-border)",
               background: isPremium ? "rgba(255,183,3,0.08)" : "var(--app-surface-2)",
             }}>
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center rounded-xl w-12 h-12 shrink-0"
                  style={{ background: "var(--app-accent)", color: "#0a0a0a" }}>
              <Crown className="w-6 h-6" />
            </span>
            <div className="flex-1">
              <div className="font-display text-xl font-bold">
                {t("Studio Pro")}
                {isPremium && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full align-middle"
                        style={{ background: "var(--app-accent)", color: "#0a0a0a" }}>{t("ACTIVE")}</span>
                )}
              </div>
              <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {!isSoloStudent && !tier?.solo_learner
                  ? <>{t("Scale beyond 75 students.")} <b>+${tier.premium_addon_usd.toFixed(2)}/mo</b> (${(tier.base_price_usd + tier.premium_addon_usd).toFixed(2)}/mo {t("total) unlocks:")}</>
                  : <>{t("Just")} <b>+${tier.premium_addon_usd.toFixed(2)}/mo</b> (${(tier.base_price_usd + tier.premium_addon_usd).toFixed(2)}/mo {t("total) unlocks:")}</>}
              </p>
              <ul className="mt-2 text-sm space-y-1">
                {!isSoloStudent && !tier?.solo_learner && <li className="flex gap-2 items-start"><Sparkles className="w-4 h-4 mt-0.5" style={{ color: "var(--app-accent)" }} /> <b>{t("Unlimited students")}</b> {t("(grow past 75 without limits)")}</li>}
                <li className="flex gap-2 items-start"><Sparkles className="w-4 h-4 mt-0.5" style={{ color: "var(--app-accent)" }} /> {t("Add custom instruments not on the list — your own photo + title")}</li>
                <li className="flex gap-2 items-start"><Sparkles className="w-4 h-4 mt-0.5" style={{ color: "var(--app-accent)" }} /> {t("Bonus app themes (sunset, midnight, arcade…)")}</li>
                <li className="flex gap-2 items-start"><Sparkles className="w-4 h-4 mt-0.5" style={{ color: "var(--app-accent)" }} /> {t("Upload a custom app background image")}</li>
              </ul>
              <Button
                onClick={subscribePremium}
                disabled={busy}
                className={`btn-pill mt-4 h-11 ${isPremium ? "btn-outline" : "btn-accent"}`}
                data-testid="toggle-premium"
              >
                {busy
                  ? t("Redirecting...")
                  : isPremium
                    ? t("Renew Studio Pro") + " ($" + (tier.base_price_usd + tier.premium_addon_usd).toFixed(2) + "/mo)"
                    : `${t("Upgrade to Studio Pro")} ($${(tier.base_price_usd + tier.premium_addon_usd).toFixed(2)}/mo)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isSoloStudent && (
        <div className="mt-8 rounded-3xl p-6 border-2" style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }} data-testid="upgrade-teacher-box">
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center rounded-xl w-12 h-12 shrink-0" style={{ background: "var(--app-primary)", color: "#fff" }}>
              <GraduationCap className="w-6 h-6" />
            </span>
            <div className="flex-1">
              <div className="font-display text-xl font-bold">{t("I'm becoming a teacher and need to add students")}</div>
              <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {t("Flip your account to a teacher studio. You'll get a")} <b>{t("fresh 30-day trial")}</b>, {t("an invite link for your students, and access to calendar, invoicing, curriculum, and recitals. Teacher pricing stays at")} <b>$9.99/mo</b> {t("up to 75 students.")}
              </p>
              <Button onClick={upgradeToTeacher} disabled={upgrading} className="btn-pill btn-primary mt-3 h-11" data-testid="upgrade-to-teacher">
                {upgrading ? t("Upgrading...") : t("Become a teacher →")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Referral nudge — earn free months by inviting other musicians */}
      <Link to="/referrals" data-testid="subscribe-referral-nudge"
            className="mt-8 block rounded-3xl p-5 border-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
            style={{ borderColor: "var(--app-accent)", background: "rgba(255,183,3,0.08)" }}>
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center rounded-xl w-10 h-10 shrink-0"
                style={{ background: "var(--app-accent)", color: "#0a0a0a" }}>
            <Gift className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="font-display font-bold text-lg leading-tight">{t("Invite musicians, earn free months")}</div>
            <div className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
              {t("Every friend who signs up with your link and subscribes adds")} <b style={{ color: "var(--app-text)" }}>{t("30 free days")}</b> {t("to your plan. No cap — invite as many as you like.")}
            </div>
          </div>
          <span className="text-sm font-bold" style={{ color: "var(--app-primary)" }}>{t("Get your link →")}</span>
        </div>
      </Link>
    </div>
  );
}

export function SubscribeSuccessPage() {
  const t = useT();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [statusInfo, setStatusInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const MAX_ATTEMPTS = 30;   // 30 × 2s = 60 seconds total
    const poll = async (n = 0) => {
      if (!sessionId || cancelled) return;
      if (n >= MAX_ATTEMPTS) { setStatusInfo({ payment_status: "timeout" }); return; }
      try {
        // Try the new recurring-subscription status first; if unknown session (404),
        // fall back to the legacy one-time-payment status endpoint.
        let r;
        try {
          r = await apiClient.get(`/subscription/checkout-status/${sessionId}`);
        } catch (err) {
          if (err?.response?.status === 404) {
            r = await apiClient.get(`/payments/status/${sessionId}`);
          } else {
            throw err;
          }
        }
        setStatusInfo(r.data); setAttempts(n + 1);
        if (r.data.payment_status === "paid") { await refresh(); return; }
        if (r.data.status === "expired") return;
        if (!cancelled) setTimeout(() => poll(n + 1), 2000);
      } catch {
        // Silent retry on transient network/Stripe lag — never toast errors mid-poll
        if (!cancelled) setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid     = statusInfo?.payment_status === "paid";
  const timedOut = statusInfo?.payment_status === "timeout";
  const pending  = !statusInfo || (!paid && !timedOut && statusInfo?.status !== "expired");

  return (
    <div className="max-w-xl mx-auto text-center py-16" data-testid="subscribe-success-page">
      {pending && (
        <>
          <div className="text-6xl mb-4 animate-pulse">🎼</div>
          <h1 className="font-display text-3xl font-bold">{t("Confirming your payment…")}</h1>
          <p className="mt-2" style={{ color: "var(--app-text-muted)" }}>{t("This usually takes a few seconds.")}</p>
        </>
      )}
      {!pending && paid && (
        <>
          <div className="text-7xl mb-4 animate-pop">🎉</div>
          <h1 className="font-display text-4xl font-bold">{t("Subscription active")}</h1>
          <p className="mt-3" style={{ color: "var(--app-text-muted)" }}>{t("Thank you! Your account renews automatically every month — manage it anytime from the Subscribe page.")}</p>
          <Button onClick={() => nav("/teacher")} className="mt-6 btn-pill btn-primary h-11" data-testid="back-to-dashboard">{t("Back to Dashboard")}</Button>
        </>
      )}
      {timedOut && (
        <>
          <div className="text-6xl mb-4">📨</div>
          <h1 className="font-display text-3xl font-bold">{t("Payment received — finalizing…")}</h1>
          <p className="mt-3" style={{ color: "var(--app-text-muted)" }}>
            {t("Stripe is taking a little longer than usual. Your subscription will activate shortly. You can safely return to your dashboard — we'll update it automatically as soon as Stripe confirms.")}
          </p>
          <Button onClick={() => nav("/teacher")} className="mt-6 btn-pill btn-primary h-11" data-testid="timeout-back">{t("Back to Dashboard")}</Button>
        </>
      )}
      {!pending && !paid && !timedOut && (
        <>
          <XCircle className="w-12 h-12 mx-auto" style={{ color: "var(--app-accent)" }} />
          <h1 className="font-display text-3xl font-bold mt-2">{t("Payment didn't complete")}</h1>
          <p className="mt-2" style={{ color: "var(--app-text-muted)" }}>{t("Please try again.")}</p>
          <Button onClick={() => nav("/subscribe")} className="mt-6 btn-pill btn-primary h-11" data-testid="retry-subscribe">{t("Try again")}</Button>
        </>
      )}
    </div>
  );
}
